import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '../api'
import { useAuth } from '../auth'
import Filters from '../components/Filters'
import TaskDialog from '../components/TaskDialog'
import TicketDialog from '../components/TicketDialog'
import { formatDate, formatHM, monthBounds } from '../lib/format'
import type { Task, TaskFilters, WorkStatus } from '../types'
import { BOARD_COLUMNS, WORK_STATUS_LABEL } from '../types'

export default function Board() {
  const qc = useQueryClient()
  const { can } = useAuth()
  const [filters, setFilters] = useState<TaskFilters>(() => {
    const { from, to } = monthBounds()
    return { date_from: from, date_to: to }
  })
  const [dragId, setDragId] = useState<number | null>(null)
  const [overColumn, setOverColumn] = useState<WorkStatus | null>(null)
  const [editing, setEditing] = useState<Task | null>(null)
  // Заявка, открытая из задачи
  const [ticketCard, setTicketCard] = useState<number | null>(null)
  const [error, setError] = useState('')

  const tasks = useQuery({
    queryKey: ['tasks', filters],
    queryFn: () => api.tasks(filters),
  })

  const move = useMutation({
    mutationFn: ({ id, status, order }: { id: number; status: WorkStatus; order: number }) =>
      api.moveTask(id, status, order),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks'] })
      qc.invalidateQueries({ queryKey: ['kpi'] })
      qc.invalidateQueries({ queryKey: ['running'] })
    },
    onError: (e: any) => setError(e?.message ?? 'Не удалось перенести задачу'),
  })

  const columns = useMemo(() => {
    const map = new Map<WorkStatus, Task[]>(BOARD_COLUMNS.map((s) => [s, []]))
    for (const t of tasks.data ?? []) {
      const list = map.get(t.work_status)
      if (list) list.push(t)
    }
    for (const list of map.values()) {
      list.sort((a, b) => Number(a.board_order) - Number(b.board_order) || b.id - a.id)
    }
    return map
  }, [tasks.data])

  function handleDrop(status: WorkStatus) {
    setOverColumn(null)
    if (dragId === null) return
    const task = (tasks.data ?? []).find((t) => t.id === dragId)
    setDragId(null)
    if (!task) return

    const target = columns.get(status) ?? []
    if (task.work_status === status && target[0]?.id === task.id) return
    // кладём в начало колонки: порядок — дробное число, пересчёт соседей не нужен
    const topOrder = target.length ? Number(target[0].board_order) - 1 : 0
    move.mutate({ id: task.id, status, order: topOrder })
  }

  return (
    <>
      <h1 className="page-title">Канбан</h1>

      {error && (
        <div className="error" onClick={() => setError('')}>
          {error} (нажмите, чтобы скрыть)
        </div>
      )}

      <Filters value={filters} onChange={setFilters} showStatus={false} />

      {tasks.isLoading ? (
        <div className="empty">Загрузка…</div>
      ) : (
        <div className="board">
          {BOARD_COLUMNS.map((status) => {
            const list = columns.get(status) ?? []
            const total = list.reduce((s, t) => s + t.tracked_seconds, 0)
            return (
              <div
                key={status}
                className={`column${overColumn === status ? ' drop-target' : ''}`}
                onDragOver={(e) => {
                  e.preventDefault()
                  setOverColumn(status)
                }}
                onDragLeave={() => setOverColumn((c) => (c === status ? null : c))}
                onDrop={(e) => {
                  e.preventDefault()
                  handleDrop(status)
                }}
              >
                <h3>
                  <span>{WORK_STATUS_LABEL[status]}</span>
                  <span className="count">{list.length}</span>
                </h3>
                <div className="col-total" style={{ marginBottom: 8 }}>
                  {formatHM(total)}
                </div>

                {list.map((t) => (
                  <div
                    key={t.id}
                    className={`card-task ${t.priority}${dragId === t.id ? ' dragging' : ''}`}
                    draggable
                    onDragStart={() => setDragId(t.id)}
                    onDragEnd={() => {
                      setDragId(null)
                      setOverColumn(null)
                    }}
                    onDoubleClick={() => setEditing(t)}
                    title="Двойной клик — открыть задачу"
                  >
                    <div className="t">{t.title}</div>
                    <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                      <span className="task-num">№ {t.id}</span> · {t.client_name}
                    </div>
                    {t.is_support && <span className="support-chip">Сопровождение</span>}
                    <div className="meta">
                      <span>{formatDate(t.work_date)}</span>
                      <span>{formatHM(t.tracked_seconds)}</span>
                    </div>
                    {t.running_since && (
                      <div style={{ fontSize: 12, color: 'var(--green)', marginTop: 4 }}>
                        ● таймер идёт
                      </div>
                    )}
                  </div>
                ))}

                {list.length === 0 && (
                  <div style={{ color: 'var(--muted)', fontSize: 12, padding: '12px 4px' }}>
                    Перетащите карточку сюда
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {editing && (
        <TaskDialog
          task={editing}
          onClose={() => setEditing(null)}
          onOpenTicket={setTicketCard}
        />
      )}

      {ticketCard !== null && (
        <TicketDialog
          id={ticketCard}
          mayReply={can('tickets.reply')}
          onClose={() => setTicketCard(null)}
        />
      )}
    </>
  )
}
